import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import DropDown from "@components/ui/dropdown/DropDown";
import { sample_repertorys } from "@screens/sampleData";
import CheckBoxInput from "@components/ui/selection/CheckBoxInput";
import { SwitchBrick } from "@components/ui/selection/SwitchBrick";
import Button from "@components/ui/buttons/Button";
import Dropzone from "@components/ui/blocks/Dropzone";
import { useState } from "react";
import TextField from "@components/ui/textfields/TextField";
import { ContactInfos, PartnerInfos, Project } from "types";
import { updateNested, updateNestedArray } from "helpers/updateHelpers";
import AreaTextField from "@components/ui/textfields/AreaTextField";
import { ChunkyTabs } from "@components/ui/selection/ChunkyTabs";
import { MemberAssignmentBlock } from "@components/ui/blocks/MemberAssignmentBlock";
import { CreateContactModal } from "@components/modals/CreateContactModal";
import { showNotification } from "context/notification.service";
import { projectsApi } from "@services/api/projects";
import { invitationsApi } from "@services/api/invitations";
import { AddPartnerToProjectModal } from "@components/modals/AddPartnerToProjectModal";
import { PLANNING_SECTIONS } from "@utils/planning_constants";
import { useProjectStore } from "@store/projectStore";
import { geocodeAddress } from "@helpers/geocodingHelper";

export const ModifyProjectModal = ({
  onClose,
  project,
  setProject,
}: {
  onClose: () => void;
  project?: any;
  setProject?: any;
}) => {
  const [droppedFiles, setDroppedFiles] = useState<File[]>([]);
  const [selectedRepertory, setSelectedRepertory] = useState<string>(
    sample_repertorys[0],
  );
  const [localProject, setLocalProject] = useState<Project>(project);
  const [electronicSignature, setElectronicSignature] =
    useState<boolean>(false);
  const [internalDocument, setInternalDocument] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>("Général");
  const [showCreateContact, setShowCreateContact] = useState<boolean>(false);
  const [showAddPartner, setShowAddPartner] = useState<boolean>(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [confirmReset, setConfirmReset] = useState<boolean>(false);
  const projectTemplates = useProjectStore((state) => state.projectTemplates);
  const updateProject = useProjectStore((state) => state.updateProject);

  async function handleSave() {
    // Linked contacts: any contact with user_id not already in guest_user_ids
    const existingGuestIds = new Set<string>(project?.guest_user_ids || []);
    const linkedContacts = (localProject.infos.client.contacts || []).filter(
      (c: ContactInfos) => c.user_id && !existingGuestIds.has(c.user_id),
    );
    if (linkedContacts.length > 0) {
      await projectsApi.update(localProject.id, {
        guest_user_ids: linkedContacts.map((c: ContactInfos) => c.user_id!),
      });
    }

    // Email-only contacts: any contact with email not already in original contacts and no user_id
    const originalContactEmails = new Set<string>(
      (project?.infos?.client?.contacts || [])
        .map((c: ContactInfos) => c.email)
        .filter(Boolean),
    );
    const emailOnlyContacts = (localProject.infos.client.contacts || []).filter(
      (c: ContactInfos) =>
        !c.user_id && c.email && !originalContactEmails.has(c.email),
    );
    if (emailOnlyContacts.length > 0) {
      await Promise.allSettled(
        emailOnlyContacts.map((contact: ContactInfos) =>
          invitationsApi.create({
            invitee_email: contact.email!,
            invitee_name:
              `${contact.first_name || ""} ${contact.last_name || ""}`.trim() ||
              undefined,
            invitation_type: "web",
            context: {
              access_type: "GUEST",
              project_id: localProject.id,
            },
          }),
        ),
      );
    }

    // Geocode if address fields changed
    const addressChanged =
      localProject.infos.general.address !== project?.infos?.general?.address ||
      localProject.infos.general.city !== project?.infos?.general?.city ||
      localProject.infos.general.postal !== project?.infos?.general?.postal;

    let updatedLocalProject = localProject;
    if (addressChanged) {
      const coordinates = await geocodeAddress(
        localProject.infos.general.address,
        localProject.infos.general.city,
        localProject.infos.general.postal,
      );
      if (coordinates) {
        await projectsApi.update(localProject.id, { coordinates });
        updatedLocalProject = {
          ...localProject,
          infos: {
            ...localProject.infos,
            general: { ...localProject.infos.general, coordinates },
          },
        };
      }
    }

    if (setProject) {
      setProject(updatedLocalProject);
    }
    updateProject(updatedLocalProject);
    showNotification({
      type: "success",
      message: "Projet modifié avec succès",
    });
    onClose();
  }

  return (
    <BaseModal
      title="Modifier un projet"
      startIcon={<Icon1 name="modify" size={20}></Icon1>}
      onClose={onClose}
    >
      <div className="bg-white flex flex-col gap-2 p-4">
        <ChunkyTabs
          short_tabs={[
            "Général",
            "Contacts",
            "Equipe",
            "Artisans",
            "Étapes",
          ]}
          setActiveTab={(tab) => setActiveTab(tab)}
          activeTab={activeTab}
        ></ChunkyTabs>
        {activeTab === "Général" && (
          <>
            <div className="flex flex-row gap-2">
              <TextField
                label="Nom"
                placeholder="DUPONT"
                value={localProject?.infos.general.name || ""}
                onChange={(value) =>
                  setLocalProject(
                    updateNested(localProject, "infos.general.name", value),
                  )
                }
              />
              <TextField
                label="Ville"
                placeholder="Rennes"
                value={localProject?.infos.general.city || ""}
                onChange={(value) =>
                  setLocalProject(
                    updateNested(localProject, "infos.general.city", value),
                  )
                }
              />
            </div>
            <div className="flex flex-row gap-2">
              <TextField
                label="Postal"
                placeholder="35000"
                value={localProject?.infos.general.postal || ""}
                onChange={(value) =>
                  setLocalProject(
                    updateNested(localProject, "infos.general.postal", value),
                  )
                }
              />
              <TextField
                label="Adresse"
                placeholder="1 rue des fleurs"
                value={localProject?.infos.general.address || ""}
                onChange={(value) =>
                  setLocalProject(
                    updateNested(localProject, "infos.general.address", value),
                  )
                }
              />
            </div>
            <AreaTextField
              label="Notes"
              placeholder="Notes sur le projet"
              value={localProject?.infos.general.notes || ""}
              onChange={(value) =>
                setLocalProject(
                  updateNested(localProject, "infos.general.notes", value),
                )
              }
            />
          </>
        )}
        {activeTab === "Contacts" && (
          <>
            <div className="flex flex-col">
              {localProject?.infos.client.contacts.map(
                (contact: ContactInfos, index: number) => (
                  <div
                    key={index}
                    className="flex flex-col gap-2 mb-4 p-4 rounded-xl border"
                  >
                    <div className="flex justify-between items-center">
                      <span className="text-h3 font-medium text-neutral-500">
                        Contact {index + 1}
                      </span>
                      <SecondaryButton
                        danger={true}
                        variant="small"
                        onClick={() =>
                          setLocalProject({
                            ...localProject,
                            infos: {
                              ...localProject.infos,
                              client: {
                                ...localProject.infos.client,
                                contacts:
                                  localProject.infos.client.contacts.filter(
                                    (_, i) => i !== index,
                                  ),
                              },
                            },
                          })
                        }
                      >
                        Supprimer
                      </SecondaryButton>
                    </div>
                    <div className="flex flex-row gap-2">
                      <TextField
                        label="Prénom"
                        placeholder="Jean"
                        value={contact.first_name || ""}
                        onChange={(value) =>
                          setLocalProject(
                            updateNestedArray(
                              localProject,
                              "infos.client.contacts",
                              index,
                              { first_name: value },
                            ),
                          )
                        }
                      />
                      <TextField
                        label="Nom"
                        placeholder="Dupont"
                        value={contact.last_name || ""}
                        onChange={(value) =>
                          setLocalProject(
                            updateNestedArray(
                              localProject,
                              "infos.client.contacts",
                              index,
                              { last_name: value },
                            ),
                          )
                        }
                      />
                    </div>
                    <div className="flex flex-row gap-2">
                      <TextField
                        label="Téléphone"
                        placeholder="06 01 02 03 04"
                        type="phone"
                        value={contact.phone_number || ""}
                        onChange={(value) =>
                          setLocalProject(
                            updateNestedArray(
                              localProject,
                              "infos.client.contacts",
                              index,
                              { phone_number: value },
                            ),
                          )
                        }
                      />
                      <TextField
                        label="Email"
                        placeholder="marie.curie@life.com"
                        value={contact.email || ""}
                        onChange={(value) =>
                          setLocalProject(
                            updateNestedArray(
                              localProject,
                              "infos.client.contacts",
                              index,
                              { email: value },
                            ),
                          )
                        }
                      />
                    </div>
                  </div>
                ),
              )}
            </div>
            <SecondaryButton
              startIcon={<Icon1 name="plus" size={18}></Icon1>}
              label="Nouveau contact"
              variant="large"
              onClick={() => setShowCreateContact(true)}
            />
            {showCreateContact && (
              <CreateContactModal
                onClose={() => setShowCreateContact(false)}
                onSave={(newContact) => {
                  setLocalProject({
                    ...localProject,
                    infos: {
                      ...localProject.infos,
                      client: {
                        ...localProject.infos.client,
                        contacts: [
                          ...localProject.infos.client.contacts,
                          newContact,
                        ],
                      },
                    },
                  });
                }}
              />
            )}
          </>
        )}
        {activeTab === "Equipe" && (
          <>
            <MemberAssignmentBlock
              team={localProject.infos.team}
              onTeamChange={(team) =>
                setLocalProject({
                  ...localProject,
                  infos: { ...localProject.infos, team },
                })
              }
            />
          </>
        )}
        {activeTab === "Artisans" && (
          <>
            {showAddPartner && (
              <AddPartnerToProjectModal
                onClose={() => setShowAddPartner(false)}
                onSelect={(partner: PartnerInfos) => {
                  if (
                    localProject.infos.partners.some((p) => p.id === partner.id)
                  )
                    return;
                  setLocalProject({
                    ...localProject,
                    infos: {
                      ...localProject.infos,
                      partners: [...localProject.infos.partners, partner],
                    },
                  });
                  setShowAddPartner(false);
                }}
                existingPartnerIds={localProject.infos.partners.map(
                  (p) => p.id,
                )}
              />
            )}
            <div className="flex flex-col gap-1">
              {PLANNING_SECTIONS.map((section) => {
                const assigned = localProject.infos.partners.find(
                  (p) => p.company_type === section.key,
                );
                return (
                  <div
                    key={section.key}
                    className={`flex flex-row items-center justify-between px-4 py-2.5 rounded-lg border transition-colors ${
                      assigned
                        ? "bg-white border-neutral-200"
                        : "bg-neutral-50 border-neutral-200 cursor-pointer hover:bg-neutral-100"
                    }`}
                    onClick={
                      !assigned ? () => setShowAddPartner(true) : undefined
                    }
                  >
                    <div className="flex flex-row items-center gap-2 flex-1">
                      <div
                        className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${assigned ? section.cellColor : "bg-neutral-300"}`}
                      />
                      <div className="flex flex-col">
                        <div
                          className={`text-h3 font-medium ${assigned ? "text-black" : "text-neutral-400"}`}
                        >
                          {section.label}
                        </div>
                        {assigned && (
                          <div className="text-h4 text-neutral-400">
                            {assigned.company_name || assigned.display_name}
                          </div>
                        )}
                      </div>
                    </div>
                    {assigned ? (
                      <SecondaryButton
                        danger={true}
                        variant="small"
                        onClick={() => {
                          setLocalProject({
                            ...localProject,
                            infos: {
                              ...localProject.infos,
                              partners: localProject.infos.partners.filter(
                                (p) => p.id !== assigned.id,
                              ),
                            },
                          });
                        }}
                      >
                        Retirer
                      </SecondaryButton>
                    ) : (
                      <Icon1 name="plus" size={14} color="grey" />
                    )}
                  </div>
                );
              })}
            </div>
            {/* Partners whose type is not a planning section specialty */}
            {localProject.infos.partners
              .filter(
                (p) => !PLANNING_SECTIONS.some((s) => s.key === p.company_type),
              )
              .map((partner) => (
                <div
                  key={partner.id}
                  className="flex flex-row items-center justify-between px-4 py-3 rounded-lg border"
                >
                  <div className="flex flex-col">
                    <div className="text-h2 text-black font-medium">
                      {partner.company_name || partner.display_name}
                    </div>
                    <div className="text-h3 text-neutral-400">
                      {partner.role}
                      {partner.company_name && partner.display_name
                        ? ` - ${partner.display_name}`
                        : ""}
                    </div>
                  </div>
                  <SecondaryButton
                    danger={true}
                    variant="small"
                    onClick={() => {
                      setLocalProject({
                        ...localProject,
                        infos: {
                          ...localProject.infos,
                          partners: localProject.infos.partners.filter(
                            (p) => p.id !== partner.id,
                          ),
                        },
                      });
                    }}
                  >
                    Retirer
                  </SecondaryButton>
                </div>
              ))}
            <SecondaryButton
              startIcon={<Icon1 name="plus" size={16} />}
              label="Ajouter un intervenant"
              variant="large"
              onClick={() => setShowAddPartner(true)}
            />
          </>
        )}
        {activeTab === "Étapes" && (
          <div className="flex flex-col gap-4">
            <div className="text-h3 text-neutral-500">
              Réinitialiser les thèmes, étapes et points de contrôle à partir
              d'un modèle de projet.
            </div>
            <div className="rounded-xl border border-orange-200 bg-orange-50 p-4 flex flex-col gap-3">
              <div className="flex items-center gap-2 text-orange-600 font-medium text-h3">
                <Icon1 name="warning" size={16} color="orange" />
                Cette action remplacera tous les thèmes, étapes et points de
                contrôle existants du projet.
              </div>
              <DropDown
                label="Modèle de projet"
                value={selectedTemplateId}
                options={[
                  { value: "", label: "— Choisir un modèle —" },
                  ...projectTemplates.map((t) => ({
                    value: t.id,
                    label: t.infos.general.name,
                  })),
                ]}
                onSelect={(v) => {
                  setSelectedTemplateId(v as string);
                  setConfirmReset(false);
                }}
              />
              {selectedTemplateId && !confirmReset && (
                <SecondaryButton
                  danger
                  variant="small"
                  onClick={() => setConfirmReset(true)}
                >
                  Réinitialiser depuis ce modèle
                </SecondaryButton>
              )}
              {selectedTemplateId && confirmReset && (
                <div className="flex flex-col gap-2">
                  <div className="text-h3 text-orange-700 font-medium">
                    Confirmer la réinitialisation ?
                  </div>
                  <div className="flex gap-2">
                    <Button
                      label="Confirmer"
                      onClick={() => {
                        const template = projectTemplates.find(
                          (t) => t.id === selectedTemplateId,
                        );
                        if (!template) return;
                        setLocalProject({
                          ...localProject,
                          themes: structuredClone(template.themes),
                        });
                        setConfirmReset(false);
                        setSelectedTemplateId("");
                        showNotification({
                          type: "success",
                          message:
                            "Étapes réinitialisées — cliquez sur Enregistrer pour sauvegarder.",
                        });
                      }}
                    />
                    <SecondaryButton
                      variant="small"
                      onClick={() => setConfirmReset(false)}
                    >
                      Annuler
                    </SecondaryButton>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>
          Annuler
        </SecondaryButton>
        <Button
          variant="small"
          label="Enregistrer"
          onClick={handleSave}
        ></Button>
      </div>
    </BaseModal>
  );
};
